/**
 * Startup Performance Service
 * 시작 성능 계측 및 단계 관리
 */

// Startup phases in order
export type StartupPhase =
  | 'bootstrapping'
  | 'vault-resolving'
  | 'shell-ready'
  | 'documents-loading'
  | 'restoring-tabs'
  | 'background-tasks'
  | 'ready'

// Startup timing markers
export type StartupMarker =
  | 'startup:renderer-mounted'
  | 'startup:vault-path-resolved'
  | 'startup:shell-visible'
  | 'startup:documents-loaded'
  | 'startup:active-tab-ready'
  | 'startup:all-tabs-ready'
  | 'startup:index-init-start'
  | 'startup:index-ready'
  | 'startup:ai-init-start'
  | 'startup:ai-ready'

interface TimingEntry {
  marker: StartupMarker
  timestamp: number
  elapsed: number // ms since startup:renderer-mounted
  source: 'renderer' | 'main'
}

class StartupService {
  private startTime: number = 0
  private timings: TimingEntry[] = []
  private _phase: StartupPhase = 'bootstrapping'
  private phaseListeners: Set<(phase: StartupPhase) => void> = new Set()
  private _isComplete: boolean = false

  /**
   * Begin startup measurement. Call once when renderer mounts.
   */
  begin(): void {
    this.startTime = performance.now()
    this.timings = []
    this._phase = 'bootstrapping'
    this._isComplete = false
    this.mark('startup:renderer-mounted')
  }

  /**
   * Record a timing marker
   */
  mark(marker: StartupMarker, source: 'renderer' | 'main' = 'renderer'): void {
    const now = performance.now()
    const elapsed = this.startTime > 0 ? now - this.startTime : 0

    this.timings.push({
      marker,
      timestamp: now,
      elapsed: Math.round(elapsed),
      source
    })

    // Log in development and production for diagnostics
    console.debug(`[Startup] ${marker}: ${Math.round(elapsed)}ms (${source})`)
  }

  /**
   * Transition to a new startup phase
   */
  setPhase(phase: StartupPhase): void {
    if (this._phase === phase) return
    this._phase = phase
    console.debug(`[Startup] Phase: ${phase}`)

    if (phase === 'ready') {
      this._isComplete = true
      this.printReport()
    }

    // Notify listeners
    for (const listener of this.phaseListeners) {
      try { listener(phase) } catch {}
    }
  }

  /**
   * Get current startup phase
   */
  get phase(): StartupPhase {
    return this._phase
  }

  /**
   * Whether startup is fully complete
   */
  get isComplete(): boolean {
    return this._isComplete
  }

  /**
   * Subscribe to phase changes
   */
  onPhaseChange(listener: (phase: StartupPhase) => void): () => void {
    this.phaseListeners.add(listener)
    return () => this.phaseListeners.delete(listener)
  }

  /**
   * Get all recorded timings
   */
  getTimings(): TimingEntry[] {
    return [...this.timings]
  }

  /**
   * Get elapsed time since startup began
   */
  getElapsed(): number {
    return this.startTime > 0 ? Math.round(performance.now() - this.startTime) : 0
  }

  /**
   * Print a summary report to console
   */
  private printReport(): void {
    if (this.timings.length === 0) return

    console.group('[Startup] Performance Report')
    console.table(
      this.timings.map(t => ({
        Marker: t.marker,
        'Elapsed (ms)': t.elapsed,
        Source: t.source
      }))
    )

    const total = this.getElapsed()
    console.log(`Total startup time: ${total}ms`)
    console.groupEnd()
  }

  /**
   * Reset for a fresh measurement (e.g., after vault switch)
   */
  reset(): void {
    this.startTime = 0
    this.timings = []
    this._phase = 'bootstrapping'
    this._isComplete = false
  }
}

// Singleton instance
export const startupService = new StartupService()
